import { useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Button, Container } from '@plone/components';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

import {
  setSidebarTab,
  setMetadataFocus,
} from '@plone/volto/actions/sidebar/sidebar';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import LanguageSelector from '@plone/volto/components/theme/LanguageSelector/LanguageSelector';
import Logo from '@plone/volto/components/theme/Logo/Logo';
import Image from '@plone/volto/components/theme/Image/Image';
import Navigation from '@plone/volto/components/theme/Navigation/Navigation';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';

import { useLiveData } from '@kitconcept/volto-light-theme/helpers/useLiveData';

import MobileNavigation from '@kitconcept/volto-light-theme/components/MobileNavigation/MobileNavigation';
import IntranetSearchWidget from '@kitconcept/volto-light-theme/components/SearchWidget/IntranetSearchWidget';

const InternetHeader = ({ pathname, content }) => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const dispatch = useDispatch();

  const formData = useSelector((state) => state.form.global);

  const intranetFlag = useLiveData(
    content,
    'voltolighttheme.header',
    'intranet_flag',
  );

  const headerActions = useLiveData(
    content,
    'voltolighttheme.header',
    'header_actions',
  );

  const pointToSidebar = (fieldSetName, fieldId) => {
    dispatch(setSidebarTab(0));
    dispatch(setMetadataFocus(fieldSetName, fieldId));
  };

  return (
    <>
      <div className="header">
        <div className="tools-wrapper">
          <LanguageSelector />

          <div className="tools">
            <SlotRenderer name="headerTools" content={content} />

            {headerActions &&
              Array.isArray(headerActions) &&
              headerActions.map((item) => (
                <UniversalLink
                  key={item['@id']}
                  href={item.href?.[0]?.['@id']}
                  openLinkInNewTab={item.openInNewTab}
                >
                  {item.title}
                </UniversalLink>
              ))}
          </div>
          {intranetFlag &&
            isClient &&
            (!isEmpty(formData) ? (
              <Button
                className="intranet-flag"
                onPress={() =>
                  pointToSidebar('header customizations', 'intranet_flag')
                }
              >
                <p>{intranetFlag}</p>
              </Button>
            ) : (
              <div className="intranet-flag">
                <p>{intranetFlag}</p>
              </div>
            ))}
        </div>

        <div className="logo-nav-wrapper">
          <div className="logo">
            <Logo />
          </div>

          <Navigation pathname={pathname} />
          <MobileNavigation pathname={pathname} />

          <div className="header-actions-right">
            <UniversalLink className="tickets-cta-link" href="/tickets">
              Tickets
            </UniversalLink>
            <div className="search-wrapper navigation-desktop">
              <div className="search">
                <SearchWidget />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const IntranetHeader = ({ pathname, content }) => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const dispatch = useDispatch();

  const formData = useSelector((state) => state.form.global);

  const intranetFlag = useLiveData(
    content,
    'voltolighttheme.header',
    'intranet_flag',
  );

  const complementaryLogo = useLiveData(
    content,
    'voltolighttheme.header',
    'complementary_logo',
  );

  const complementaryLogoSrc = complementaryLogo?.data
    ? `data:${complementaryLogo['content-type']};base64,${complementaryLogo.data}`
    : flattenToAppURL(complementaryLogo?.download);

  const headerActions = useLiveData(
    content,
    'voltolighttheme.header',
    'header_actions',
  );

  const pointToSidebar = (fieldSetName, fieldId) => {
    dispatch(setSidebarTab(0));
    dispatch(setMetadataFocus(fieldSetName, fieldId));
  };

  return (
    <>
      <div className="header header-intranet">
        <div className="tools-wrapper">
          <LanguageSelector />

          <div className="tools">
            <SlotRenderer name="headerTools" content={content} />

            {headerActions &&
              Array.isArray(headerActions) &&
              headerActions.map((item) => (
                <UniversalLink
                  key={item['@id']}
                  href={item.href?.[0]?.['@id']}
                  openLinkInNewTab={item.openInNewTab}
                >
                  {item.title}
                </UniversalLink>
              ))}
          </div>
          {intranetFlag &&
            isClient &&
            (!isEmpty(formData) ? (
              <Button
                className="intranet-flag"
                onPress={() =>
                  pointToSidebar('header customizations', 'intranet_flag')
                }
              >
                <p>{intranetFlag}</p>
              </Button>
            ) : (
              <div className="intranet-flag">
                <p>{intranetFlag}</p>
              </div>
            ))}
        </div>

        <div className="logo-nav-wrapper">
          <div className="logo">
            <Logo />
          </div>

          <div className="search-wrapper">
            <div className="search">
              <IntranetSearchWidget />
            </div>
          </div>

          <MobileNavigation pathname={pathname} />

          <div className="complementary-logo">
            {complementaryLogo && (
              <Image src={complementaryLogoSrc} alt="complementary logo" />
            )}
          </div>

          <div className="header-actions-right">
            <UniversalLink className="tickets-cta-link" href="/tickets">
              Tickets
            </UniversalLink>
          </div>
        </div>

        <Navigation pathname={pathname} />
      </div>
    </>
  );
};

const Header = (props) => {
  const { pathname } = props;
  const content = useSelector((state) => state.content.data, shallowEqual);
  const navRoot = useSelector((state) => state.navroot?.data?.navroot);

  const hasIntranetHeader = useLiveData(
    content,
    'voltolighttheme.header',
    'has_intranet_header',
  );

  return (
    <>
      <SlotRenderer name="aboveHeader" content={content} navRoot={navRoot} />
      <header
        className={cx('header-wrapper', {
          'intranet-header': hasIntranetHeader,
        })}
      >
        <Container layout>
          {hasIntranetHeader ? (
            <IntranetHeader pathname={pathname} content={content} />
          ) : (
            <InternetHeader pathname={pathname} content={content} />
          )}
        </Container>
      </header>
      <SlotRenderer name="belowHeader" content={content} navRoot={navRoot} />
    </>
  );
};

export default Header;
